
import SafeMotion from '@/components/motion/SafeMotion';
import MotionErrorBoundary from '@/components/motion/MotionErrorBoundary';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

interface ModernFeatureCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  link: string;
  linkText: string;
  gradient: string;
  bgGradient: string;
  index?: number;
}

const ModernFeatureCard = ({
  icon: Icon,
  title,
  description,
  link,
  linkText,
  gradient,
  bgGradient,
  index = 0
}: ModernFeatureCardProps) => {
  return (
    <MotionErrorBoundary>
      <SafeMotion 
        initial={{
          opacity: 0,
          y: 30,
          scale: 0.95
        }} 
        animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} 
        transition={{
          type: "spring",
          stiffness: 100,
          damping: 15,
          delay: index * 0.1
        }} 
        whileHover={{
          y: -8,
          transition: {
            duration: 0.3
          }
        }}
      >
        <Card className="border border-luxury-gold/20 h-full group overflow-hidden bg-gradient-luxury-feature backdrop-blur-sm shadow-luxury-feature hover:shadow-luxury-hover hover:border-luxury-gold/40 transition-all duration-500 hover:scale-[1.02]">
          {/* Luxury top accent */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-luxury-gold to-transparent opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
          
          <CardHeader className="text-center pb-4 sm:pb-8 relative">
            {/* Luxury background overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-luxury-gold/5 via-luxury-champagne/10 to-luxury-rose-gold/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-t-2xl" />
            
            <SafeMotion 
              className="mx-auto mb-4 sm:mb-6 p-5 bg-gradient-to-br from-luxury-gold/20 via-luxury-champagne/30 to-luxury-rose-gold/20 border border-luxury-gold/30 rounded-2xl w-fit relative z-10 shadow-luxury-feature" 
              whileHover={{
                scale: 1.15,
                rotate: [0, -8, 8, 0],
                boxShadow: "0 8px 25px -5px hsl(var(--luxury-gold) / 0.4)"
              }} 
              transition={{
                duration: 0.4,
                type: "spring",
                stiffness: 200
              }}
            >
              <Icon className="h-6 w-6 sm:h-8 sm:w-8 text-luxury-gold" />
            </SafeMotion>
            
            <CardTitle className="text-xl sm:text-2xl relative z-10 group-hover:text-luxury-gold font-bold transition-colors duration-300 leading-tight">
              {title}
            </CardTitle>
            <CardDescription className="text-sm sm:text-base relative z-10 text-muted-foreground mt-2 leading-relaxed">
              {description}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-0 pb-6 relative z-10">
            <SafeMotion
              whileHover={{ scale: 1.05, y: -2 }}
              transition={{ duration: 0.3, type: "spring", stiffness: 300 }}
            >
              <Button asChild variant="ghost" className="w-full min-h-[48px] group-hover:bg-luxury-gold/10 border border-luxury-gold/20 group-hover:border-luxury-gold/40 group-hover:text-luxury-gold transition-all duration-300 rounded-xl font-semibold relative overflow-hidden group/btn">
                <Link to={link} className="flex items-center justify-center relative z-10">
                  <span className="text-sm sm:text-base font-semibold relative">
                    {linkText}
                    <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-gradient-to-r from-luxury-gold to-luxury-rose-gold transition-all duration-300 group-hover/btn:w-full" />
                  </span>
                  <SafeMotion 
                    whileHover={{
                      x: 8,
                      scale: 1.2
                    }} 
                    transition={{
                      duration: 0.2
                    }}
                    className="ml-3 flex items-center justify-center w-6 h-6 rounded-full bg-luxury-gold/10 group-hover/btn:bg-luxury-gold/20 transition-all duration-300"
                  >
                    <ArrowRight className="h-4 w-4" />
                  </SafeMotion>
                  <div className="absolute inset-0 bg-gradient-to-r from-luxury-gold/5 to-luxury-champagne/10 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300 rounded-xl" />
                </Link>
              </Button>
            </SafeMotion>
          </CardContent>

          {/* Luxury hover background effect */}
          <SafeMotion 
            className="absolute inset-0 bg-gradient-to-br from-luxury-gold/5 via-luxury-champagne/10 to-luxury-rose-gold/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"
          />
        </Card>
      </SafeMotion>
    </MotionErrorBoundary>
  );
};

export default ModernFeatureCard;
